/**
 * /src/blocks/reward-shop/view.js
 * Handles the “Redeem” flow (no shipping pop-ups).
 * Relies solely on XXRest.nonce (localised in PHP).
 */

/* ------------------------------------------------------------------ */
/*  0. REST nonce                                                     */
/* ------------------------------------------------------------------ */
const REST_NONCE = window.XXRest?.nonce || '';

/* ------------------------------------------------------------------ */
/*  1. Click delegation                                               */
/* ------------------------------------------------------------------ */
document.addEventListener('click', async (e) => {

	/* ── Guard: only Redeem buttons ─────────────────────────────── */
	const btn = e.target.closest('.xxa-redeem-btn');
	if (!btn) return;

	// Handle login redirect for non-logged in users
	if (btn.textContent.includes('Login')) {
		window.location.href = '/xlogin?redirect_to=' + encodeURIComponent(window.location.href);
		return;
	}

	/* ── Read data attributes ───────────────────────────────────── */
	const card   = btn.closest('.xxa-reward-card');
	const itemId = Number(card.dataset.id);
	const cost   = Number(card.dataset.cost);

	/* No shipping: send empty object */
	const address = {};

	// Store original text
	const btnText = btn.querySelector('.btn-text');
	const originalText = btnText ? btnText.textContent : btn.textContent;

	// Check if we're in confirmation mode
	if (btn.classList.contains('confirm-mode')) {
		// This is the second click - proceed with redemption
		btn.disabled = true;
		card.classList.add('is-processing');
		
		if (btnText) {
			btnText.textContent = 'Processing…';
		} else {
			btn.textContent = 'Processing…';
		}
	} else {
		// First click - enter confirmation mode
		btn.classList.add('confirm-mode');
		if (btnText) {
			btnText.textContent = `Spend ${cost} MyBucks?`;
		} else {
			btn.textContent = `Spend ${cost} MyBucks?`;
		}
		
		// Reset after 5 seconds if not confirmed
		setTimeout(() => {
			if (btn.classList.contains('confirm-mode')) {
				btn.classList.remove('confirm-mode');
				if (btnText) {
					btnText.textContent = originalText;
				} else {
					btn.textContent = originalText;
				}
			}
		}, 5000);
		
		return; // Don't proceed with the redemption yet
	}

	try {
		/* ── POST /redeem ───────────────────────────────────────── */
		const resp = await fetch('/wp-json/mybucks/v1/redeem', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce':    REST_NONCE,
			},
			credentials: 'same-origin',
			body: JSON.stringify({ reward_id: itemId, address }),
		});

		const data = await resp.json();                    // parse once

		/* ── Error guards ─────────────────────────────────────── */
		// WordPress REST API returns errors without a 'success' field
		// Check if it's an error by looking for 'code' field or non-OK response
		if (!resp.ok || data.code) {

			card.classList.remove('is-processing');
			btn.classList.remove('confirm-mode');
			
			// Handle specific error codes from the backend
			switch (data.code) {
				case 'insufficient_funds':
					// The price may have changed - show the actual current price from the error message
					const priceMatch = data.message?.match(/costs (\d+)/);
					const actualCost = priceMatch ? priceMatch[1] : cost;
					window.XtraXtraToast?.show(`You don't have enough MyBucks. This reward costs ${actualCost} MyBucks.`, 'error');
					btn.disabled = false;
					if (btnText) {
						btnText.textContent = `${actualCost} MyBucks`;
					} else {
						btn.textContent = `${actualCost} MyBucks`;
					}
					return;

				case 'out_of_stock':
					window.XtraXtraToast?.show('Sorry! This reward just sold out.', 'error');
					card.classList.add('sold-out');
					btn.classList.add('limit-reached');
					btn.disabled = true;
					if (btnText) {
						btnText.textContent = 'Sold Out';
					} else {
						btn.textContent = 'Sold Out';
					}
					return;

				case 'limit_reached':
					window.XtraXtraToast?.show('You\'ve already redeemed the maximum allowed for this reward.', 'error');
					card.classList.add('user-limit-reached');
					btn.classList.add('limit-reached');
					btn.disabled = true;
					if (btnText) {
						btnText.textContent = 'Already Redeemed';
					} else {
						btn.textContent = 'Already Redeemed';
					}
					return;
			}

			console.error('Redeem error', resp.status, data);
			window.XtraXtraToast?.show(data.message || 'Something went wrong. Please refresh and try again.', 'error');
			
			// Reset button state for unknown errors
			btn.disabled = false;
			if (btnText) {
				btnText.textContent = originalText;
			} else {
				btn.textContent = originalText;
			}
			return;
		}

		/* ── SUCCESS ──────────────────────────────────────────── */
		// For success, check if we have a success field or if there's no error code
		if (resp.ok && !data.code) {
			// Show toast notification
			if (window.XtraXtraToast) {
				window.XtraXtraToast.show('Congratulations! You\'ve redeemed a reward. Check your inbox for details.', 'success');
			}
			
			// Update balance display if it exists and new balance is provided
			const balanceDisplay = document.querySelector('.xxa-balance-amount');
			if (balanceDisplay && data.new_balance !== undefined) {
				// Just update the text content, the icon is handled by CSS
				balanceDisplay.childNodes[balanceDisplay.childNodes.length - 1].textContent = data.new_balance + ' MyBucks';
				balanceDisplay.dataset.balance = data.new_balance;
			
				// Add a pulse animation to draw attention
				balanceDisplay.classList.add('balance-updated');
				setTimeout(() => {
					balanceDisplay.classList.remove('balance-updated');
				}, 1000);
			}
			
			document.dispatchEvent(
				new CustomEvent('xxa:redeem-success', { detail: data })
			);

			// Update button to success state
			btn.classList.add('redeemed');
			btn.classList.remove('confirm-mode');
			if (btnText) {
				btnText.textContent = 'Redeemed ✓';
			} else {
				btn.textContent = 'Redeemed ✓';
			}
			
			card.classList.remove('is-processing');
			card.classList.add('is-redeemed');
		}

	} catch (err) {
		console.error('Redeem catch', err);
		card.classList.remove('is-processing');
		btn.classList.remove('confirm-mode');
		btn.disabled = false;
		if (btnText) {
			btnText.textContent = originalText;
		} else {
			btn.textContent = originalText;
		}
	}
});
